import { filter } from "lodash";
import { getConfigData } from "../../services/configurationService";
import { getDataFromLocalDb, insertDataIntoLocalDB } from "../../services/dbService";
import { ChannelkartNetworkGet } from "../../services/networkService";
import { getNewConfiguration } from "../../services/uiConfigService";
import { MultiQuantities, OrderType } from "../../types/types";
import { getCurrentDateTime } from "../../utils/calenderUtils";
import { keysToKeep, keysToKeepOD } from "../viewOrder/ViewOrderUtils";
import { UOM } from "./CreateSecondaryOrderType";
import { Catalogue, Outlet, CartProduct, Cart, ProductUI, OutletDetails } from "./models";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";
import { safeStringify } from "../../components/dms_common/DmsUtils";
import { AggregatedStockData } from "../../services/manageOrderService";

export const fetchAllDataFromApi = async (url: string, size: number): Promise<any[]> => {
  let allData: any[] = [];
  let page = 0;
  try {
    while (true) {
      const response = await ChannelkartNetworkGet(`${url}&size=${size}&page=${page}`);
      const data = response?.data?.features ?? [];
      allData.push(...data);
      if (data.length < size) {
        break;
      }
      page++;
    }
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from API",
      `Failed to fetch data from the API: ${url}`,
      safeStringify({ error,url,size,page})
    );
    console.error("Error fetching data from the API", error);
    throw new Error(`Error fetching data from the API: ${url}`);
  }
  return allData;
};

export const fetchAllDataFromApiNew = async (url: string, size: number): Promise<any[]> => {
  let allData: any[] = [];
  let page = 0;
  try {
    while (true) {
      const response = await ChannelkartNetworkGet(`${url}${url.includes("?") ? "&" : "?"}size=${size}&page=${page}`);
      const data = response?.data?.features ?? [];
      allData.push(...data);
      if (data.length < size) {
        break;
      }
      page++;
    }
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from API",
      `Failed to fetch data from the API: ${url}`,
      safeStringify({ error,url,size,page})
    );
    console.error("Error fetching data from the API", error);
    throw new Error(`Error fetching data from the API: ${url}`);
  }
  return allData;
};

export const fetchSkuReplacementMapping = async () => {
  let skuReplacementData : any[] = [];
  const size = 10000;
  const filter = `/v1/entitymapping/dynamic?filter=entityCategory:productreplacement and entityType:product`
  try {
    skuReplacementData = await fetchAllDataFromApi(filter, size);
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from API",
      `Failed to fetch data from the API: ${filter}`,
      safeStringify({ error,filter,size})
    );
    console.error("Error fetching data from the API", error);
    throw new Error(`Error fetching data from the API: ${filter}`);
  }
  return skuReplacementData;
};

export const fetchSiteWarehouseMapping = async () => {
  let siteWarehouseMapping: any[] = [];
  let userWarehouseMap: Record<string, string[]> = {}; 
  const size = 10000;

  const sourceFilter = JSON.parse(localStorage.authContext)?.user?.source;

  let filter = `/warehouse?filter=type:warehouse and warehouseCategory:FG`;
  if (sourceFilter && String(sourceFilter).trim() !== "") {
    filter += ` and source:${encodeURIComponent(sourceFilter)}`;
  }

  try {
    siteWarehouseMapping = await fetchAllDataFromApiNew(filter, size);

    const tempMap: Record<string, Set<string>> = siteWarehouseMapping.reduce((acc, { userName, warehouseCode }) => {
      if (!acc[userName]) {
        acc[userName] = new Set();
      }
      acc[userName].add(warehouseCode);
      return acc;
    }, {} as Record<string, Set<string>>);

    for (const user in tempMap) {
      userWarehouseMap[user] = Array.from(tempMap[user]);
    }

  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from API",
      `Failed to fetch data from the API: ${filter}`,
      safeStringify({ error, filter, size })
    );
    console.error("Error fetching data from the API", error);
    throw new Error(`Error fetching data from the API: ${filter}`);
  }

  return userWarehouseMap; 
};
export const fetchPaginatedStockData = async (url: string, size: number): Promise<any[]> => {
  let allData: any[] = [];
  let page = 0;
  try {
    while (true) {
      const response = await ChannelkartNetworkGet(`${url}${url.includes("?") ? "&" : "?"}size=${size}&index=${page}`);
      const data = response?.data?.content ?? [];
      allData.push(...data);
      if (data.length === 0) {
        break;
      }
      page++;
    }
  } catch (error) {
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from API",
      `Failed to fetch data from the API: ${url}`,
      safeStringify({ error,url,size,page})
    );
    console.error("Error fetching data from the API", error);
    throw new Error(`Error fetching data from the API: ${url}`);
  }
  return allData;
};


export const getOutletDetailsForOutletCodeList = async (outletCode: string[]) => {
  if(outletCode.length === 0) return [];
  let outletData = await getDataFromLocalDb("OutletList");
  let allOutlets: OutletDetails[] = outletData.data;

  if (!allOutlets || allOutlets.length === 0) {
    allOutlets = await fetchOutletDetails();
    await insertDataIntoLocalDB("OutletList", allOutlets);
  }

  return allOutlets.filter((s: OutletDetails) => outletCode.includes(s.outletCode));
};

export const getOutletDetails = async (outletCode?: string) => {
  let outletData = await getDataFromLocalDb("OutletList");
  let allOutlets: OutletDetails[] = outletData.data;
  if (allOutlets?.length === 0) {
    allOutlets = await fetchOutletDetails();
    await insertDataIntoLocalDB("OutletList", allOutlets);
  }
  return outletCode === undefined
  ? allOutlets
  : allOutlets?.filter((s: OutletDetails) => s.outletCode === outletCode);
};

export const fetchOutletDetails = async (): Promise<OutletDetails[]> => {
  let size = 1000;
  const currentUserData = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user;
  const designations: string[] = currentUserData?.designation ?? [];
  let filterClause = "";

  const ignoreOutletTypeFilter = await getConfigData("dms_configuration","ignoreOutletTypeFilterForSuperStockist");
  const outletDetailsAPISizeParam = await getConfigData("dms_configuration","outletDetailsAPISizeParam");
  size = Number(outletDetailsAPISizeParam[0]?.configValue ?? 1000);
  const ignoreOutletTypeFilterValue = ignoreOutletTypeFilter[0]?.configValue ?? false;
  
  if (designations.includes("superstockist") && !ignoreOutletTypeFilterValue) {
    filterClause = "outletType:subdistributor";
  }


  const minimalResponseOutletDetailsApi = await getConfigData("dms_configuration","minimalResponseOutletDetailsApi");
  const minimalResponseOutletValue = minimalResponseOutletDetailsApi[0]?.configValue ?? false

  let minimalRes = false;
  if(minimalResponseOutletValue){
    minimalRes = true
  }

  const base = "/outletDetails?minimalResponse="+ minimalRes;
  let filterPart = filterClause
    ? `&filter=${encodeURIComponent(filterClause)}`
    : "";
  const url = `${base}${filterPart}`;
  
  const response: any[] = await fetchAllDataFromApi(url, size) as OutletDetails[];

  const outletList: OutletDetails[] = response.map((item) => ({
    ...item,
    _meta: item._meta ?? {},
  }));

  return outletList;
};

export const getOutletDetailsForSecondary = async (loginId?: string) => {
  let outletData = await getDataFromLocalDb("OutletList");
  let allOutlets: OutletDetails[] = outletData.data;
  if (allOutlets?.length === 0) {
    allOutlets = await fetchOutletDetails();
    await insertDataIntoLocalDB("OutletList", allOutlets);
  }
  return allOutlets?.filter((s: any) => s?.hierarchy?.includes(loginId + " >"));
};


export const getPieceFilterMapping = (label: string): string => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const filtersMap: any = ConfigDictionary?.pieceSizeLabelMapping ?? {}

  if (filtersMap?.[label]) {
    return filtersMap?.[label];
  }
  return label === "NA" ? "" : label;
};

/**
 * Generates filter options from a catalogue based on specified filter keys.
 *
 * This function processes a list of catalogue items and extracts unique values
 * for each specified key in the `newFiltersConfig`. It returns an object where
 * each key corresponds to a filter and its value is an array of unique strings
 * representing the possible filter options.
 *
 * Special handling is applied to the `pieceSize` key to ensure numerical sorting.
 * Other filters are sorted alphabetically.
 *
 * @param catalogue - An array of Catalogue items to extract filter options from.
 * @param newFiltersConfig - An array of keys from the Catalogue used to generate filters.
 * @returns An object mapping each filter key to an array of unique string options.
 */

export const getFilterOptionsUnique = (
  catalogue: Catalogue[],
  newFiltersConfig: (keyof Catalogue)[]
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

 try{
  newFiltersConfig.forEach((key) => {
    const uniqueSet = new Set<string>();

    catalogue.forEach((product) => {
      const value = product[key];

      // Handle undefined/null
      if (value !== undefined && value !== null) {
        const stringValue = value.toString().trim();
        if (stringValue) uniqueSet.add(stringValue);
      }
    });

    result[key] = Array.from(uniqueSet);

    // Special handling for pieceSize sorting
    if (key === "pieceSize") {
      result[key] = result[key].sort((a, b) => Number(a) - Number(b));
    } else {
      result[key] = result[key].sort(); // Optional: alphabetically sort others
    }
  });

 }catch(error) {
  console.error("Error while fetching the filters", error);
 } 
  return result;
};


export const getFilterOptions = async (catalogue: Catalogue[], filters?: any) => {
  try {
    const allFilters: any = filters ? filters : await ChannelkartNetworkGet(
      `/products/v1/filter?domainName=filters&domainType=tab`
    );
    const uniqueCategories = Array.from(new Set(catalogue.map((product) => product.category)));
    const uniqueBrands = Array.from(new Set(catalogue.map((product) => product.brand)));
    const uniquePieceSizes = Array.from(
      new Set(catalogue.map((product) => product.pieceSize?.toString()))
    );

    const result: Record<string, string[]> = {};
    allFilters?.data?.features?.forEach((item: Record<string, any>) => {
      Object.keys(item).forEach((key) => {
        const uniqueData =
          key === "category" ? uniqueCategories : key === "brand" ? uniqueBrands : uniquePieceSizes;
        if (!result[key]) {
          result[key] = [];
        }
        const values = Array.isArray(item[key]) ? item[key] : [item[key]];
        values?.forEach((value: string) => {
          const splitValues = value?.split(",")?.map((v: any) => v.trim());
          splitValues?.forEach((splitValue: string) => {
            if (uniqueData.includes(splitValue) && !result[key].includes(splitValue)) {
              result[key].push(splitValue);
            }
          });
        });
      });
    });
    // sort piece sizes in ascending order
    if (result["pieceSize"]) {
      result["pieceSize"] = result["pieceSize"]?.sort((a, b) => Number(a) - Number(b));
    }
    return result;
  } catch (error) {
    console.error("Error while fetching the filters", error);
  }
};

export const createCartProductFromCatalogue = (
  catalogueList: Catalogue[],
  batchCode: string
): CartProduct | null => {
  const foundCatalogue = catalogueList.find((catalogue) => catalogue.batchCode === batchCode);
  if (!foundCatalogue) {
    return null;
  }

  const product: CartProduct = {
    id: foundCatalogue.id,
    lastModified: new Date(),
    manufacturingDateV1: new Date(),
    refId: foundCatalogue.skuCode,
    batchCode: foundCatalogue.batchCode,
    skuCode: foundCatalogue.skuCode,
    imageUrl: foundCatalogue.imageUrl,
    name: foundCatalogue.name,
    outletCode: "",
    pieceQuantity: 0,
    caseQuantity: 0,
    otherUnitQuantity: 0,
    qtyUnit: "",
    amount: 0,
    otherUnitName: foundCatalogue.otherUnitName,
    uom: foundCatalogue.uom,
    price: foundCatalogue.packPtr,
    casePrice: foundCatalogue.casePtr,
    otherUnitPrice: foundCatalogue.otherUnitPtr,
    margin: foundCatalogue.discountMargin,
    netPtr: foundCatalogue.packPtr,
    maxDiscount: 0,
    volume: 0,
    savings: 0,
    skuDesc: foundCatalogue.name,
    visitedOffers: [],
    supplier: foundCatalogue.supplier,
    itemDescV1: foundCatalogue.itemDescV1,
    stockQtyV1: foundCatalogue.stockQty,
    typeV1: foundCatalogue.typeV1,
    creditQty: 0,
    ssp: foundCatalogue.srp,
    discountMargin: foundCatalogue.discountMargin,
    sbu: foundCatalogue.sbu,
    mrp: foundCatalogue.mrp,
    caseMrp: foundCatalogue.caseMrp,
    otherUnitMrp: foundCatalogue.otherUnitMrp,
    case2Piece: foundCatalogue.case2Piece,
    otherUnit2Piece: foundCatalogue.otherUnitToPieceQuantity,
    pieceSize: foundCatalogue.pieceSize,
    availableCaseQty: foundCatalogue.availableCaseQty,
    availablePieceQty: foundCatalogue.availablePieceQty,
    availableOtherQty: foundCatalogue.availableOtherQty,
    schemes: foundCatalogue.schemes,
    minimumOrderQuantity: foundCatalogue.minimumOrderQuantity,
    mov: foundCatalogue.mov,
    moqtype: foundCatalogue.moqtype,
    moqUOM: foundCatalogue.moqUom,
  };
  return product;
};

export const calculateTotalAmountAndQuantity = (
  totalQuantity: number,
  totalAmount: number,
  batchCode: string,
  newCaseQuantity: number,
  oldCaseQuantity: number,
  newPieceQuantity: number,
  oldPieceQuantity: number,
  newOtherUnitQuantity: number = 0,
  oldOtherUnitQuantity: number = 0,
  catalogue: Catalogue[],
  type: UOM
) => {
  const foundCatalogue = catalogue.find((item) => item?.batchCode === batchCode);

  if (!foundCatalogue) {
    return { totalAmount, totalQuantity };
  }

  const casePtr = foundCatalogue?.casePtr ?? 0;
  const packPtr = foundCatalogue?.packPtr ?? 0;
  const otherUnitPtr = foundCatalogue?.otherUnitPtr ?? 0;

  const updatedProductAmount =
    type === UOM.CASE
      ? (newCaseQuantity - oldCaseQuantity) * casePtr
      : type === UOM.OTHER
      ? (newOtherUnitQuantity - oldOtherUnitQuantity) * otherUnitPtr
      : (newPieceQuantity - oldPieceQuantity) * packPtr;

  const updatedProductQuantity =
    type === UOM.CASE
      ? newCaseQuantity - oldCaseQuantity
      : type === UOM.OTHER
      ? newOtherUnitQuantity - oldOtherUnitQuantity
      : newPieceQuantity - oldPieceQuantity;

  return {
    totalAmount: updatedProductAmount + totalAmount,
    totalQuantity: updatedProductQuantity + totalQuantity,
  };
};

export const createQuery = (
  searchValue: string | null | undefined,
  filters: Record<string, string>
) => {
  const query: { [key: string]: any } = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== "ALL") query[key] = { $in: [value] };
  }
  if (searchValue) {
    query["searchValue"] = { $regex: searchValue };
  }
  return query;
};

export const getCurrentDateTimeFormat = (): string => {
  const now: Date = new Date();

  const padZero = (num: number): string => num.toString().padStart(2, "0");

  const year: number = now.getFullYear();
  const month: string = padZero(now.getMonth() + 1);
  const date: string = padZero(now.getDate());
  const hours: string = padZero(now.getHours());
  const minutes: string = padZero(now.getMinutes());
  const seconds: string = padZero(now.getSeconds());

  return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;
};

const getTotalQuantityAndAmount = (products: CartProduct[]) => {
  let totalAmount = 0;
  let totalQuantity = 0;
  products?.forEach((product) => {
    const { pieceQuantity, caseQuantity, otherUnitQuantity, price, casePrice, otherUnitPrice } =
      product;
    totalQuantity += pieceQuantity + caseQuantity + otherUnitQuantity;
    totalAmount +=
      pieceQuantity * price + caseQuantity * casePrice + otherUnitQuantity * otherUnitPrice;
  });
  return {
    totalAmount,
    totalQuantity,
  };
};

export function formatEditedQuantities(editedCQ: number, editedPQ: number, editedOUQ: number): string {
  let result: string[] = [];

  // Add case quantity if it's greater than 0
  if (editedCQ > 0) {
    result.push(`${editedCQ} Case${editedCQ > 1 ? 's' : ''}`);
  }

  // Add piece quantity if it's greater than 0
  if (editedPQ > 0) {
    result.push(`${editedPQ} Piece${editedPQ > 1 ? 's' : ''}`);
  }

  // Add other unit quantity if it's greater than 0
  if (editedOUQ > 0) {
    result.push(`${editedOUQ} Other${editedOUQ > 1 ? 's' : ''}`);
  }

  // Join all parts with a comma and space, and return the result
  return result.join(', ') || 'No quantities edited';
}

export const isMOQEnabled = (designation: string[] ,enableMOQCheckConfig: {enable: boolean, rolesAllowedForMOQ: string[]}) => {
  let enableMOQCheck = false;
  const allowedRoles = enableMOQCheckConfig?.rolesAllowedForMOQ ?? [];
  if (allowedRoles.length === 0) {
    enableMOQCheck = true;
  } else {
    enableMOQCheck = designation.some((role: string) => allowedRoles.includes(role));
  }
  return enableMOQCheck
}

export const isARSSkuDetail = (orderdetails: any):boolean => {
  return orderdetails?.source?.toLowerCase() === "ars" || false;
}

export const getValidateRequestBody = (outletCart: Outlet, orderData?: any, useSalesRepAsLoginId?: boolean, isCounterSaleOrder?:boolean, outletReferenceId?: string, includeSupplier?: boolean,outletType?:string,setSiteWarehousePrimaryOMS?:boolean, outletData?: any, isPrimary?: boolean) => {
  const currentUserData = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user;
  const designations = currentUserData?.designation ?? [];
  let isSuperStockist: boolean = designations.includes("superstockist")
  const products: CartProduct[] = Object.values(outletCart?.productsMap || {});
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    usePromoServiceForSchemes,
    subDistributorDesignations,
    useSalesrepAsLoginIdForSubD
  } = ConfigDictionary;
  if (products.length === 0) {
    return null;
  }
  const isCounterSaleOrderValue = isCounterSaleOrder ?? orderData?.extendedAttributes?.isCounterSaleOrder ?? false;
  const outletReferenceIdValue = outletReferenceId ?? orderData?.extendedAttributes?.outletReferenceId ?? "";
  const subdDesignations: string[] = subDistributorDesignations ?? [];
  const isSubdDesignation: boolean = subdDesignations.some((d: string) => designations.includes(d));
  
  const designation = JSON.parse(localStorage.authContext).user.designation || [];
  
  let enableMOQCheck = false;

  const enableMOQCheckConfig = ConfigDictionary?.enableMOQCheck;
  if (enableMOQCheckConfig?.enable) enableMOQCheck = isMOQEnabled(designation, enableMOQCheckConfig)
  

  const { totalAmount, totalQuantity } = getTotalQuantityAndAmount(products);
  const outletCode = outletCart?.customer?.outletCode?.toString() || "";
  const loginId = ((useSalesRepAsLoginId && useSalesRepAsLoginId) && (outletCart?.customer?.["selectedSalesRep"])) ? outletCart.customer?.["selectedSalesRep"] : JSON.parse(localStorage.authContext)?.user?.loginId;
  let beat = outletCart?.customer?.["beat"];
  beat = beat?.trim()?.length === 0 ? null : beat;

  const salesRepLoginId = (useSalesrepAsLoginIdForSubD && isSubdDesignation && isPrimary) ? (outletData?.extendedAttributes?.["parent-0-0-id"] ?? loginId) : loginId;

  const getOrderDetail = (batchCode: string) => 
    orderData?.orderDetails?.find((detail: any) => detail.batchCode === batchCode);

  const getSupplierPrice = (prod: any, field:any) => {
    if (!includeSupplier) return prod[field] || 0;
    
    const orderDetail = getOrderDetail(prod.batchCode);
    return (orderDetail && isARSSkuDetail(orderDetail)) 
      ? orderDetail[field] || 0 
      : prod[field] || 0;
  };
  const calculateTotalByField = (field: string) => 
  products.reduce((acc:any, prod:any) => acc + (prod[field] || 0), 0);

  const currentDateTime = getCurrentDateTimeFormat();
  const isSelOutlet = outletCode === "self";


  return {
    outletCode: outletCode === "self" ? loginId : outletCode,
    loginId: (useSalesrepAsLoginIdForSubD && isSubdDesignation && isPrimary) ? salesRepLoginId : loginId,
    beat: beat,
    billAmount: totalAmount,
    netAmount: totalAmount,
    lineCount: products.length,
    totalQuantity: totalQuantity,
    totalBilledQuantity: totalQuantity,
    orderDetails: products.map((prod) => {
      const orderDetail = getOrderDetail(prod.batchCode);
      
      return {
        batchCode: prod.batchCode || "",
        skuCode: prod.skuCode || "",
        price: getSupplierPrice(prod, 'price'),
        casePrice: getSupplierPrice(prod, 'casePrice'),
        otherUnitPrice: getSupplierPrice(prod, 'otherUnitPrice'),
        pieceQuantity: prod.pieceQuantity || 0,
        caseQuantity: prod.caseQuantity || 0,
        otherUnitQuantity: prod.otherUnitQuantity || 0,
        supplier: includeSupplier ? orderData?.supplier : prod.supplier || "",
        type: "normal",
        extendedAttributes: {
          pieceSize: prod.pieceSize || 0,
          caseToPieceQty: prod.case2Piece || 0,
          blobKey: prod?.imageUrl || "",
          ...(enableMOQCheck && {
            moqValue: prod?.minimumOrderQuantity ?? 0,
            moqUOM: prod?.moqUOM ?? "",
            moqtype: prod?.moqtype ?? "",
            mov: prod?.mov ?? 0
          }),
        },
        discountInfo: null,
        source: orderDetail?.source ?? "DMS"
      };
    }),
    remarks: orderData?.remarks ? orderData?.remarks : "",
    finalOrderInfo: {
      quantity: products.map((prod) => ({
        batchCode: prod.batchCode || "",
        caseQuantity: prod.caseQuantity || 0,
        pieceQuantity: prod.pieceQuantity || 0,
        otherUnitQuantity: prod.otherUnitQuantity || 0,
      })),
      finalAmount: totalAmount.toString(),
    },
    source: orderData?.source || "DMS",
    status: orderData?.status || "PENDING",
    referenceNumber: orderData?.referenceNumber || `${loginId}-${Date.now()}`,
    submissionTime: currentDateTime,
    
    type: (() => {
      if ( (isSuperStockist && !isSelOutlet) || isSubdDesignation || outletType === "subdistributor") return "subd";
      if (orderData?.type) return orderData.type;
      return isSelOutlet ? "primary" : "VisitOrder";
    })(),
    
    ...(orderData?.subType && { subType: orderData.subType }),
    
    totalCaseQuantity: calculateTotalByField('caseQuantity'),
    totalPieceQuantity: calculateTotalByField('pieceQuantity'),
    totalOtherUnitQuantity: calculateTotalByField('otherUnitQuantity'),
    totalAmount,
    creationTime: currentDateTime,
    freeItemInfo: [],
    discountInfo: null,
    
    extendedAttributes: {
      skuCodeSortOrder: products.map(prod => prod.skuCode || ""),
      ...(usePromoServiceForSchemes && { isNewSchemes: true }),
      ...(isCounterSaleOrderValue && {isCounterSaleOrder: isCounterSaleOrderValue ?? false, outletReferenceId: outletReferenceIdValue ?? ""}),
      ...(ConfigDictionary?.showSiteWarehouseFilterViewPrimaryOMS && setSiteWarehousePrimaryOMS && {
        siteId: orderData?.siteId ?? "",
        warehouseId: orderData?.warehouseId ?? "",
      }),
    }
  };
};
export const getValidateBodyForSKUReplacement = (
  orderData?: any,
  newOrderDataAfterReplacement?: { products: any[]; summary: any },
  isCounterSaleOrder?:boolean, 
  outletReferenceId?: string,
  includeSupplier?: boolean
,) => {

  const updatedProducts = newOrderDataAfterReplacement?.products ?? [];
  
  const getOrderDetail = (batchCode: string) =>
    orderData?.orderDetails?.find(
      (detail: any) => detail.batchCode === batchCode
    );
  const getSupplierPrice = (prod: any, field:any) => {
    if (!includeSupplier) return prod[field] || 0;
    
    const orderDetail = getOrderDetail(prod.batchCode);
    return (orderDetail && isARSSkuDetail(orderDetail)) 
      ? orderDetail[field] || 0 
      : prod[field] || 0;
  };

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {
    usePromoServiceForSchemes,
  } = ConfigDictionary;
  if (newOrderDataAfterReplacement?.products.length === 0) {
    return null;
  }
 
  const designation = JSON.parse(localStorage.authContext).user.designation || [];
  
  let enableMOQCheck = false;

  const enableMOQCheckConfig = ConfigDictionary?.enableMOQCheck;
  if (enableMOQCheckConfig?.enable) enableMOQCheck = isMOQEnabled(designation, enableMOQCheckConfig)
  const currentDateTime = getCurrentDateTimeFormat();
  
  // compute totals from products
  const {totalCaseQuantity, totalPieceQuantity, totalOtherUnitQuantity, totalAmount} = newOrderDataAfterReplacement?.summary

  const totalQuantity =
    totalCaseQuantity + totalPieceQuantity + totalOtherUnitQuantity;

  return {
    outletCode: orderData.outletCode,
    loginId: orderData.loginId,
    billAmount: totalAmount,
    netAmount: totalAmount,
    lineCount: updatedProducts.length,
    totalQuantity,
    totalBilledQuantity: totalQuantity,

    orderDetails: updatedProducts.map((prod) => {
      const orderDetail = getOrderDetail(prod.batchCode);

      return {
        batchCode: prod.batchCode || "",
        skuCode: prod.skuCode || "",
        price: prod.price ?? getSupplierPrice(prod, "price"),
        casePrice: prod.casePrice ?? getSupplierPrice(prod, "casePrice"),
        otherUnitPrice:
          prod.otherUnitPrice ?? getSupplierPrice(prod, "otherUnitPrice"),
        pieceQuantity: prod.pieceQuantity || 0,
        caseQuantity: prod.caseQuantity || 0,
        otherUnitQuantity: prod.otherUnitQuantity || 0,
        supplier: includeSupplier ? orderData?.supplier : prod.supplier || "",
        type: "normal",
        extendedAttributes: {
          pieceSize: prod.pieceSize || 0,
          caseToPieceQty: prod.caseToPieceQty || 0,
          blobKey: prod?.imageUrl || "",
          ...(enableMOQCheck && {
            moqValue: prod?.minimumOrderQuantity ?? 0,
            moqUOM: prod?.moqUOM ?? "",
            moqtype: prod?.moqtype ?? "",
            mov: prod?.mov ?? 0,
          }),
        },
        discountInfo: null,
        source: orderDetail?.source ?? "DMS",
      };
    }),

    remarks: orderData?.remarks ?? "",
    finalOrderInfo: {
      quantity: updatedProducts.map((prod) => ({
        batchCode: prod.batchCode || "",
        caseQuantity: prod.caseQuantity || 0,
        pieceQuantity: prod.pieceQuantity || 0,
        otherUnitQuantity: prod.otherUnitQuantity || 0,
      })),
      finalAmount: totalAmount.toString(),
    },
    source: orderData?.source || "DMS",
    status: orderData?.status || "PENDING",
    referenceNumber:
      orderData?.referenceNumber || `${orderData.loginId}-${Date.now()}`,
    submissionTime: currentDateTime,

    type: "VisitOrder",

    ...(orderData?.subType && { subType: orderData.subType }),

    totalCaseQuantity,
    totalPieceQuantity,
    totalOtherUnitQuantity,
    totalAmount,
    creationTime: currentDateTime,
    freeItemInfo: [],
    discountInfo: null,

    extendedAttributes: {
      skuCodeSortOrder: updatedProducts.map((p) => p.skuCode || ""),
      ...(usePromoServiceForSchemes && { isNewSchemes: true }),
      ...(isCounterSaleOrder && {
        isCounterSaleOrder: isCounterSaleOrder ?? false,
        outletReferenceId: outletReferenceId ?? "",
      }),
    },
  };
};



export const updateQuantityInCart = async (
  id: string,
  newQuantity: number,
  type: UOM,
  customerInfo: Record<string, any> = {},
  allCatalogue: Catalogue[] = [],
  setCart: React.Dispatch<React.SetStateAction<Cart>>
) => {
  const outletCode = customerInfo?.outletCode ?? "";
  if (newQuantity >= 0 && allCatalogue) {
    setCart((prevCart) => {
      const updatedCart = { ...prevCart };

      if (!updatedCart[outletCode]) {
        updatedCart[outletCode] = {
          productsMap: {},
          customer: customerInfo,
        };
      }

      const customer = { ...updatedCart[outletCode] };
      customer.productsMap = { ...customer.productsMap };

      const oldCaseQuantity = customer?.productsMap[id]?.caseQuantity || 0;
      const oldPieceQuantity = customer?.productsMap[id]?.pieceQuantity || 0;
      const oldOtherUnitQuantity = customer?.productsMap[id]?.otherUnitQuantity || 0;

      if (
        newQuantity === 0 &&
        (type === UOM.CASE
          ? !oldPieceQuantity && !oldOtherUnitQuantity
          : type === UOM.OTHER
          ? !oldCaseQuantity && !oldPieceQuantity
          : !oldCaseQuantity && !oldOtherUnitQuantity)
      ) {
        const { [id]: removedProduct, ...remainingProducts } = customer.productsMap;
        customer.productsMap = remainingProducts;
      } else {
        if (customer.productsMap[id]) {
          customer.productsMap[id] = {
            ...customer.productsMap[id],
            ...(type === UOM.CASE
              ? { caseQuantity: newQuantity }
              : type === UOM.OTHER
              ? { otherUnitQuantity: newQuantity }
              : { pieceQuantity: newQuantity }),
          };
        } else {
          const product = createCartProductFromCatalogue(allCatalogue, id);
          if (product) {
            customer.productsMap[id] = {
              ...product,
              ...(type === UOM.CASE
                ? { caseQuantity: newQuantity }
                : type === UOM.OTHER
                ? { otherUnitQuantity: newQuantity }
                : { pieceQuantity: newQuantity }),
            };
          }
        }
      }
      updatedCart[outletCode] = customer;
      insertDataIntoLocalDB("Cart", [{ id: 1, data: updatedCart }]);
      return updatedCart;
    });
  }
};

export const updateMultiQtyInCart = (
  id: string,
  convertedQtyObj: MultiQuantities,
  customerInfo: Record<string, any> = {},
  allCatalogue: Catalogue[] = [],
  setCart: React.Dispatch<React.SetStateAction<Cart>>
) => {
  const outletCode = customerInfo?.outletCode ?? "";
  if (allCatalogue) {
    setCart((prevCart) => {
      const updatedCart = { ...prevCart };

      if (!updatedCart[outletCode]) {
        updatedCart[outletCode] = {
          productsMap: {},
          customer: customerInfo,
        };
      }

      const customer = { ...updatedCart[outletCode] };
      customer.productsMap = { ...customer.productsMap };

      if (Object.values(convertedQtyObj).every((qty) => qty === 0)) {
        const { [id]: removedProduct, ...remainingProducts } = customer.productsMap;
        customer.productsMap = remainingProducts;
      } else {
        if (customer.productsMap[id]) {
          customer.productsMap[id] = {
            ...customer.productsMap[id],
            ...convertedQtyObj,
          };
        } else {
          const product = createCartProductFromCatalogue(allCatalogue, id);
          if (product) {
            customer.productsMap[id] = {
              ...product,
              ...convertedQtyObj,
            };
          }
        }
      }
      updatedCart[outletCode] = customer;
      insertDataIntoLocalDB("Cart", [{ id: 1, data: updatedCart }]);
      return updatedCart;
    });
  }
};

const getFinalOrderInfo = (orderValidateResponse: any) => {
  const qtyArr = orderValidateResponse["orderDetails"]?.map((obj: any) => {
    return {
      batchCode: obj.batchCode,
      caseQuantity: obj.caseQuantity,
      otherUnitQuantity: obj.otherUnitQuantity,
      pieceQuantity: obj.pieceQuantity,
    };
  });
  return {
    finalAmount: orderValidateResponse?.billAmount,
    quantity: qtyArr,
  };
};

export const prepareOrderReqBody = (
  orderValidateResponse: any,
  orderType?: OrderType,
  customerInfo?: any
) => {
  const keysToRemove = [
    "changed",
    "changes",
    "channel",
    "couponValue",
    "create",
    "hierarchy",
    "initialNormalizedQuantity",
    "lob",
    "locationHierarchy",
    "normalizedQuantity",
    "normalizedQuantity",
    "outletMobile",
    "outletName",
    "processingStatus",
    "remainingCredit",
    "repeatOrder",
    "salesValue",
    "supplierHierarchy",
    "supplierMobile",
    "supplierName",
    "systemGeneratedOrderNumber",
    "totalCredit",
    "totalDiscountValue",
    "totalInitialAmt",
    "totalInitialQuantity",
    "totalMrp",
    "update",
    "validate",
    "orderNumber",
  ];

  const filteredOrderValidateResponse: { [key: string]: any } = {};

  keysToKeep.forEach((key) => {
    if (orderValidateResponse.hasOwnProperty(key)) {
      filteredOrderValidateResponse[key] = orderValidateResponse[key];
      if (key === "orderDetails") {
        let orderDetailsFullNodes = filteredOrderValidateResponse[key];
        const filteredOrderDetailsArray: any = [];

        orderDetailsFullNodes.forEach((orderDetailsFullNode: any) => {
          const filteredOrderDetails: { [key: string]: any } = {};
          keysToKeepOD.forEach((odKey: any) => {
            if (orderDetailsFullNode.hasOwnProperty(odKey)) {
              filteredOrderDetails[odKey] = orderDetailsFullNode[odKey];
            }
          });
          filteredOrderDetailsArray.push(filteredOrderDetails);
        });

        filteredOrderValidateResponse[key] = filteredOrderDetailsArray;
      }
    }
  });
  orderValidateResponse = filteredOrderValidateResponse;

  // keysToRemove.forEach((key) => delete orderValidateResponse[key]);
  orderValidateResponse["creationTime"] = getCurrentDateTime();
  orderValidateResponse["submissionTime"] = getCurrentDateTime();
  if (customerInfo?.billToAddress) {
    orderValidateResponse["extendedAttributes"]["billingAddress"] = customerInfo?.billToAddress;
  }
  if (customerInfo?.diffShippingAddress && Boolean(customerInfo?.shipToAddress)) {
    orderValidateResponse["extendedAttributes"]["shippingAddress"] = customerInfo?.shipToAddress;
  }
  orderValidateResponse["finalOrderInfo"] = getFinalOrderInfo(orderValidateResponse);
  delete orderValidateResponse["extendedAttributes"]["orderValidate"];
  delete orderValidateResponse["extendedAttributes"]["billTo"];
  delete orderValidateResponse["extendedAttributes"]["shipTo"];
  orderValidateResponse["orderDetails"] = orderValidateResponse["orderDetails"]?.filter(
    (product: any) => product.initialNormalizedQuantity > 0
  );
  orderValidateResponse["orderDetails"] = orderValidateResponse["orderDetails"].map((obj: any) => {
    return {
      batchCode: obj.batchCode,
      casePrice: obj.casePrice,
      caseQuantity: obj.caseQuantity,
      creditQuantity: obj.creditQuantity,
      discountInfo: obj.discountInfo,
      extendedAttributes: obj.extendedAttributes,
      otherUnitPrice: obj.otherUnitPrice,
      otherUnitQuantity: obj.otherUnitQuantity,
      pieceQuantity: obj.pieceQuantity,
      price: obj.price,
      skuCode: obj.skuCode,
      supplier: obj.supplier,
      source: obj?.source ?? "DMS",
      type: obj.type,
    };
  });
  return orderValidateResponse;
};

function adjustQty(calculatedQty: number, originalQty: number, isPendingOrder: boolean) {
  if (isPendingOrder) {
    // For pending orders: negative quantities become 0
    return calculatedQty < 0 ? 0 : calculatedQty;
  } else {
    // For non-pending: 0 or less become original availableQty
    return calculatedQty <= 0 ? originalQty : calculatedQty;
  }
}
export const getProductUIList = (responseCatalogue: any, cartProductsMap: any, isPendingOrder:boolean, aggregatedStockMap?: Map<string, AggregatedStockData>) => {
  return responseCatalogue?.data?.map((catalogueItem: Catalogue) => {
    const cartItem: CartProduct = cartProductsMap[catalogueItem?.batchCode];
    let aggregatedSkuData: AggregatedStockData = aggregatedStockMap?.get(catalogueItem?.batchCode + "-normal") ?? {
      type: "normal",
      batchCode: catalogueItem?.batchCode,
      caseQuantity: 0,
      pieceQuantity: 0,
      otherUnitQuantity: 0
    };

    const product: ProductUI = {
      isFavourite: catalogueItem?.isFavourite,
      isRecommended: catalogueItem?.isRecommended,
      name: catalogueItem?.name,
      level: catalogueItem?.level,
      description: catalogueItem?.description,
      batchCode: catalogueItem?.batchCode,
      marketSkuCode: catalogueItem?.marketSkuCode,
      pieceQuantity: cartItem?.pieceQuantity || 0,
      caseQuantity: cartItem?.caseQuantity || 0,
      otherUnitQuantity: cartItem?.otherUnitQuantity || 0,
      suggestedQuantity: catalogueItem?.suggestedQty,
      suggestedCaseQuantity: catalogueItem?.suggestedCaseQty,
      suggestedOtherQuantity: catalogueItem?.suggestedOtherQty,
      priority: catalogueItem?.priority,
      images: catalogueItem?.images,
      imageUrl: catalogueItem?.imageUrl,
      category: catalogueItem?.category,
      brand: catalogueItem?.brand,
      price: catalogueItem?.packPtr,
      casePrice: catalogueItem?.casePtr,
      amount: 0,
      margin: 0,
      netPtr: 0,
      maxDiscount: 0,
      volume: catalogueItem?.pieceSize,
      savings: 0,
      referenceNo: "",
      otherUnitToPieceQuantity: catalogueItem?.otherUnitToPieceQuantity,
      caseMrp: catalogueItem?.caseMrp,
      pieceSize: catalogueItem?.pieceSize,
      otherUnitMrp: 0,
      time: "",
      otherUnitPrice: catalogueItem?.otherUnitPtr,
      qtyUnit: "",
      mrp: catalogueItem?.mrp,
      productTag: catalogueItem?.productTag,
      outletCode: "",
      caseToPieceQty: catalogueItem?.case2Piece,
      feature: "",
      underperformanceAttr: "",
      searchIndex: catalogueItem?.searchIndex,
      supplier: catalogueItem?.supplier,
      actualPtr: 0,
      isDiscountAvailable: false,
      tax: 0,
      pieceToOtherUnitQuantity: 0,
      otherUnitName: catalogueItem?.otherUnitName,
      additionalSchemeDescription: "",
      smartBuy: "",
      isUpdated: false,
      subCategory: "",
      isUOMChanged: false,
      refId: "",
      stockQty: 0,
      groupIdV1: "",
      maxBenefitStringV1: "",
      recommendedAttribures: catalogueItem?.recommendedAttributesV2,
      itemDescV1: "",
      uom: catalogueItem?.uom,
      typeV1: "",
      bandedMaxQuantityV1: 0,
      minQtyForEarnPoints: 0,
      recommendationId: catalogueItem?.recommendationId,
      recommendationPriority: catalogueItem?.recommendationPriority,
      recommendedQty: catalogueItem?.recommendedQty,
      productCode: catalogueItem?.productCode,
      productDescription: catalogueItem?.productDescription,
      creditQty: 0,
      srp: 0,
      returnCode: catalogueItem?.returnCode,
      discountMargin: 0,
      eb2bCode: catalogueItem?.eb2bCode,
      secondaryProductTag: catalogueItem?.secondaryProductTag,
      outOfStock: catalogueItem?.outOfStock,
      isNotify: catalogueItem?.isNotify,
      sbu: catalogueItem?.sbu,
      eanCode: catalogueItem?.eanCode,
      availableCaseQty: adjustQty((catalogueItem?.availableCaseQty ?? aggregatedSkuData.caseQuantity) - aggregatedSkuData.caseQuantity, catalogueItem?.availableCaseQty || aggregatedSkuData.caseQuantity, isPendingOrder),
      availablePieceQty: adjustQty((catalogueItem?.availablePieceQty ?? aggregatedSkuData.pieceQuantity) - aggregatedSkuData.pieceQuantity, catalogueItem?.availablePieceQty || aggregatedSkuData.pieceQuantity, isPendingOrder),
      availableOtherQty: adjustQty((catalogueItem?.availableOtherQty ?? aggregatedSkuData.otherUnitQuantity) - aggregatedSkuData.otherUnitQuantity, catalogueItem?.availableOtherQty || aggregatedSkuData.otherUnitQuantity, isPendingOrder),
      casePtr: catalogueItem?.casePtr,
      packPtr: catalogueItem?.packPtr,
      schemes: catalogueItem?.schemes,
      minimumOrderQuantity: catalogueItem?.minimumOrderQuantity,
    };
    return product;
  });
};
